import type { LiveBreakHost, LiveStream } from "@hobbyshopify/backend-client";
import { Rx, RxList } from "subtree.js/core";
import { type FormStatus, idleForm, idleLoad, type LoadStatus } from "../../core/primitives/load-status";
import type { ShareTarget } from "../../core/primitives/share-links";

/**
 * Three groups, because a host opening this has one of three questions and they
 * are not equally likely: what is running right now, what is coming up, and what
 * did the last one do.
 */
export interface StreamGroups {
  live: LiveStream[];
  upcoming: LiveStream[];
  finished: LiveStream[];
}

export function groupStreams(streams: readonly LiveStream[]): StreamGroups {
  return {
    live: streams.filter((s) => s.status === "LIVE"),
    upcoming: streams.filter((s) => s.status === "SCHEDULED"),
    finished: streams.filter((s) => s.status === "ENDED" || s.status === "CANCELLED"),
  };
}

/** Where a break is on its way to the front. The audience sees the same thresholds. */
export function fillBadge(soldCount: number, slotCount: number): { label: string; cls: string; pct: number } {
  const pct = slotCount === 0 ? 0 : Math.round((soldCount / slotCount) * 100);
  if (slotCount > 0 && soldCount >= slotCount) return { label: "Full", cls: "badge-accent", pct: 100 };
  if (pct >= 90) return { label: "Breaking soon", cls: "badge-danger", pct };
  if (pct >= 60) return { label: "Hot", cls: "badge-warn", pct };
  if (pct > 0) return { label: "Filling", cls: "badge-neutral", pct };
  return { label: "Just listed", cls: "badge-neutral", pct };
}

/**
 * The Live page: the queue first, because that is where the money is, then
 * the streams it has been ripped on.
 */
export class LiveStreamsState {
  readonly status = new Rx<LoadStatus>(idleLoad);
  readonly streams = new RxList<LiveStream>();
  readonly groups = new Rx<StreamGroups>({ live: [], upcoming: [], finished: [] });
  /** Every unfinished break, fullest first: on a stream or waiting for one. */
  readonly queue = new RxList<LiveBreakHost>();
  readonly queueStatus = new Rx<LoadStatus>(idleLoad);
  readonly goLive = new Rx<FormStatus>(idleForm);
  /** Streams open at the shop now, on air or about to be: one per location, any of the crew hosting. */
  readonly open = new RxList<LiveStream>();
  /** Each break's public page, by break id, for the ones buyers can see. */
  readonly breakShares = new Rx<ReadonlyMap<string, ShareTarget>>(new Map());
}

export abstract class LiveStreamsActions {
  /** The location's open stream if there is one, else the line-up picker that opens one. */
  abstract goLive(): void;
  abstract reload(): Promise<void>;
}
